/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { fromEvent, interval, Observable } from 'rxjs';
import { mapTo, share, skipWhile, switchMap, take } from 'rxjs/operators';
import { clone, isNil } from 'lodash';
import { Commands } from 'bridge-commands/commands';
import { BridgeService } from '../bridge/bridge.service';
import { Logger, LoggingService } from '../logging/logging.service';
import { WindowParams } from './window-spawn';

@Injectable({
    providedIn: 'root'
})
export class WindowSpawnService {
    private static readonly MinWindowWidth = 800;
    private static readonly MinWindowHeight = 600;
    private static readonly WindowCheckIntervalMs = 200;

    private logger: Logger;
    private windowOpenCheckInterval: Observable<number>;

    constructor(
        private bridgeService: BridgeService,
        loggingService: LoggingService
    ) {
        this.logger = loggingService.getLogger('WindowSpawnService');
        this.windowOpenCheckInterval = interval(WindowSpawnService.WindowCheckIntervalMs).pipe(share());
    }

    /**
     * Opens URL in a CEF child window
     *
     * @param url - URL of page to be loaded
     * @param name - Window name
     * @param params - Window appearance parameters
     * @param alwaysOnTop - Makes sure child window is always on top (Default: true)
     */
    public openChildWindow(url: string, name: string, params: WindowParams, alwaysOnTop = true): Window {
        const childWindow = window.open(url, name, this.getWindowParamsString(params));
        if (alwaysOnTop) {
            this.logger.info('will always keep child window on top');
            this.keepWindowFocused(childWindow);
        }
        return childWindow;
    }

    /**
     * Forces child window to be focused until it is closed
     */
    private keepWindowFocused(childWindow: Window) {
        // execute bridge commands to focus on child window whenever parent window was focused
        let windowFocused = fromEvent(window, 'focus')
            .pipe(
                switchMap((event: Event) => {
                    childWindow.focus();
                    return this.bridgeService.executeCommand(Commands.Common.Windowcontrol.Focus, {
                        name: childWindow.name
                    });
                })
            )
            .subscribe(
                () => {},
                (err: Error) => {
                    this.logger.error('Failed to focus window', err.message);
                }
            );

        // unsubscribe from parent window focus events when child window closes
        let childWindowClosed = this.windowClosed(childWindow).subscribe(() => {
            this.logger.info('child window closed');
            windowFocused.unsubscribe();
            windowFocused = null;
            childWindowClosed.unsubscribe();
            childWindowClosed = null;
        });
    }

    /**
     * Emits `true` when window is closed.
     */
    public windowClosed(handle: Window): Observable<boolean> {
        // Need to pool `closed` property of the window handle in order to determine if it was closed
        return this.windowOpenCheckInterval.pipe(
            skipWhile(() => !handle.closed),
            mapTo(true),
            take(1) // this will complete the observable and stop the timer
        );
    }

    /**
     * Returns the new window spec (i.e. a string string with commma seperated
     * list of properties and no whitespaces)
     *
     * @param windowParams
     */
    private getWindowParamsString(windowParams: WindowParams): string {
        if (isNil(windowParams)) {
            throw new Error('invalid or missing window params');
        }

        let paramsString = '';
        const params = clone(windowParams);

        // if not provided or value is 0, set width/height to default values
        params.width = params.width || WindowSpawnService.MinWindowWidth;
        params.height = params.height || WindowSpawnService.MinWindowHeight;

        // if not provided, calculate top/left
        if (isNil(params.top)) {
            params.top = window.screenY + window.innerHeight / 2 - params.height / 2;
        }
        if (isNil(params.left)) {
            params.left = window.screenX + window.innerWidth / 2 - params.width / 2;
        }

        for (const key of Object.keys(params)) {
            paramsString += `${key}=${params[key]},`;
        }

        return paramsString;
    }
}
